import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Modal, Form, Input, InputNumber, DatePicker, Row, Col, Table, Alert, Divider, Typography, Tag, Select, Button, Space, message,
} from '../../ui';
import { ArrowLeftRight, MapPin } from 'lucide-react';
import dayjs from 'dayjs';
import { request } from '../../api/client';
import { fmtQty } from '../../lib/format';
import { splitBoxLoose, displayBoxLoose } from '../../lib/packaging';
import { formatIstDate } from '../../lib/time';

type Props = {
  open: boolean;
  onClose: () => void;
  orderId: string;
};

type OrderItem = {
  id: string;
  productNameSnapshot: string;
  qty: string;
  unitPrice: string;
};

export function DispatchModal({ open, onClose, orderId }: Props) {
  const qc = useQueryClient();
  const [form] = Form.useForm();
  const [qtys, setQtys] = useState<Record<string, number>>({});
  // Box-packed lines: the packer's boxes+loose entry (source of truth once touched); qtys stays the total.
  const [boxLoose, setBoxLoose] = useState<Record<string, { box: number; loose: number }>>({});
  const [warehouseId, setWarehouseId] = useState<string | undefined>();
  // orderItemId -> (batchId -> qty): the source batch(es) the packer allocates per line.
  const [alloc, setAlloc] = useState<Record<string, Record<string, number>>>({});

  const { data: order } = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => request<any>({ url: `/orders/${orderId}` }),
    enabled: open && !!orderId,
  });

  const { data: pending } = useQuery({
    queryKey: ['order-pending', orderId],
    queryFn: () => request<Record<string, number>>({ url: `/orders/${orderId}/shipments/pending` }),
    enabled: open && !!orderId,
  });

  const { data: warehouses } = useQuery({
    queryKey: ['warehouses'],
    queryFn: () => request<any[]>({ url: '/warehouses' }),
    enabled: open,
  });
  // Live stock per product per warehouse (keyed by warehouse code) to show what
  // each warehouse can fulfil before dispatching from it.
  const { data: catalog } = useQuery({
    queryKey: ['catalog-cached'],
    queryFn: () => request<any[]>({ url: '/catalog/with-stock' }),
    enabled: open,
  });

  const dispatchWarehouses = useMemo(() => (warehouses ?? []).filter((w: any) => w.type !== 'PRODUCTION'), [warehouses]);
  // A WAREHOUSE_MANAGER can't list /warehouses, so the lookup is empty for them — fall back to the code the
  // order detail resolves server-side (the order ships from its assigned warehouse). Keeps the In-warehouse
  // column, the short-stock highlight and the "not enough stock" alert working for the WHM too.
  const whCode = (dispatchWarehouses.find((w: any) => String(w.id) === String(warehouseId))?.code
    ?? (String(warehouseId) === String(order?.dispatchWarehouseId) ? (order?.dispatchWarehouseCode as string | undefined) : undefined)) as string | undefined;
  // Warehouse is assigned at approval and locked here — the WM/packer ships from the order's warehouse.
  const assignedWh = order?.dispatchWarehouseId ? dispatchWarehouses.find((w: any) => String(w.id) === String(order.dispatchWarehouseId)) : null;
  // productId → available qty in the chosen warehouse.
  const whStockByProduct = useMemo(() => {
    const m = new Map<string, number>();
    if (whCode) for (const c of catalog ?? []) m.set(String(c.productId), Number(c.stock?.warehouses?.[whCode] ?? 0));
    return m;
  }, [catalog, whCode]);
  // Per-product box packing (from the live catalog) so the packer can pick boxes + loose.
  const packagingByProduct = useMemo(() => {
    const m = new Map<string, { enabled: boolean; upb: number }>();
    for (const c of catalog ?? []) m.set(String(c.productId), { enabled: !!c.boxPackingEnabled && Number(c.unitsPerBox) > 0, upb: Number(c.unitsPerBox) || 0 });
    return m;
  }, [catalog]);
  // Current boxes+loose for a line: the packer's entry once touched, else derived from the total (= pending).
  const blFor = (id: string, upb: number): { box: number; loose: number } => {
    if (boxLoose[id]) return boxLoose[id];
    const s = splitBoxLoose(Number(qtys[id] ?? 0), upb);
    return { box: s.boxes, loose: s.loose };
  };
  const setBox = (id: string, upb: number, box: number) => {
    const cur = blFor(id, upb); const b = Math.max(0, Math.floor(box || 0));
    setBoxLoose((s) => ({ ...s, [id]: { box: b, loose: cur.loose } }));
    setQtys((m) => ({ ...m, [id]: b * upb + cur.loose }));
  };
  const setLoose = (id: string, upb: number, loose: number) => {
    const cur = blFor(id, upb); const lo = Math.max(0, loose || 0);
    setBoxLoose((s) => ({ ...s, [id]: { box: cur.box, loose: lo } }));
    setQtys((m) => ({ ...m, [id]: cur.box * upb + lo }));
  };

  useEffect(() => {
    if (open && pending) {
      const initial: Record<string, number> = {};
      for (const [k, v] of Object.entries(pending)) initial[k] = Number(v);
      setQtys(initial);
      setBoxLoose({});
      form.resetFields();
    }
  }, [open, pending, form]);

  // Default the warehouse to wherever the order already shipped from (partial
  // dispatch), else leave empty so the packer chooses.
  useEffect(() => {
    if (open) setWarehouseId(order?.dispatchWarehouseId ? String(order.dispatchWarehouseId) : undefined);
  }, [open, order?.dispatchWarehouseId]);
  // Batches are warehouse-specific — clear allocations whenever the warehouse changes.
  useEffect(() => { setAlloc({}); }, [warehouseId]);

  const send = useMutation({
    mutationFn: (vals: any) =>
      request({
        url: `/orders/${orderId}/shipments`,
        method: 'POST',
        data: {
          ...vals,
          warehouseId,
          expectedAt: vals.expectedAt ? vals.expectedAt.toISOString() : undefined,
          lines: Object.entries(qtys)
            .filter(([_, q]) => Number(q) > 0)
            .map(([orderItemId, qty]) => ({
              orderItemId, qty: Number(qty),
              allocations: Object.entries(alloc[orderItemId] ?? {})
                .filter(([, bq]) => Number(bq) > 0)
                .map(([batchId, bq]) => ({ batchId, qty: Number(bq) })),
            })),
        },
      }),
    onSuccess: () => {
      message.success('Packed — stock committed');
      qc.invalidateQueries({ queryKey: ['order', orderId] });
      qc.invalidateQueries({ queryKey: ['order-timeline', orderId] });
      qc.invalidateQueries({ queryKey: ['order-shipments', orderId] });
      qc.invalidateQueries({ queryKey: ['order-pending', orderId] });
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Pack failed'),
  });

  const totalThisDispatch = Object.values(qtys).reduce((s, v) => s + Number(v || 0), 0);
  const totalPending = Object.values(pending ?? {}).reduce((s, v) => s + Number(v || 0), 0);
  const items: OrderItem[] = order?.items ?? [];

  const allocatedFor = (itemId: string) => Object.values(alloc[itemId] ?? {}).reduce((s, q) => s + (Number(q) || 0), 0);
  const shippingItemIds = items.filter((it) => Number(qtys[it.id] ?? 0) > 0).map((it) => String(it.id));
  // Every line being shipped must have its batch allocation sum exactly to its ship qty.
  const allBatchAllocated = items.every((it) => {
    const q = Number(qtys[it.id] ?? 0);
    return q <= 0 || Math.abs(allocatedFor(it.id) - q) < 1e-6;
  });
  // No line may pack more than its pending qty (a box+loose entry can push the total over — guard it).
  const withinPending = items.every((it) => Number(qtys[it.id] ?? 0) <= Number(pending?.[it.id] ?? 0) + 1e-6);

  // Total available across ALL warehouses, per product (to decide if consolidation can fulfil).
  const totalStockByProduct = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of catalog ?? []) {
      const whs = (c.stock?.warehouses ?? {}) as Record<string, any>;
      m.set(String(c.productId), Object.values(whs).reduce((s: number, v: any) => s + Number(v || 0), 0));
    }
    return m;
  }, [catalog]);

  // Per-product pending across the order's lines.
  const pendingByProduct = useMemo(() => {
    const m = new Map<string, number>();
    for (const it of items) {
      const pid = String((it as any).productId);
      m.set(pid, (m.get(pid) ?? 0) + Number(pending?.[it.id] ?? 0));
    }
    return m;
  }, [items, pending]);

  // Consolidation: the chosen warehouse can't cover the full pending of some line, BUT the
  // combined stock across warehouses can — so we can transfer stock in and ship as one.
  const consolidationNeeded = useMemo(() => {
    if (!whCode) return false;
    for (const [pid, need] of pendingByProduct) if (need > (whStockByProduct.get(pid) ?? 0) + 1e-6) return true;
    return false;
  }, [whCode, pendingByProduct, whStockByProduct]);
  const consolidationFeasible = useMemo(() => {
    if (!consolidationNeeded) return false;
    for (const [pid, need] of pendingByProduct) if (need > (totalStockByProduct.get(pid) ?? 0) + 1e-6) return false;
    return true;
  }, [consolidationNeeded, pendingByProduct, totalStockByProduct]);

  const { data: plan } = useQuery({
    queryKey: ['consolidation-plan', orderId, warehouseId],
    queryFn: () => request<any[]>({ url: `/orders/${orderId}/consolidation-plan`, params: { toWarehouseId: warehouseId } }),
    enabled: open && !!orderId && !!warehouseId && consolidationFeasible,
  });

  const consolidate = useMutation({
    mutationFn: () => request({ url: `/orders/${orderId}/consolidate`, method: 'POST', data: { toWarehouseId: warehouseId } }),
    onSuccess: () => {
      message.success('Consolidating stock — order held until the transfers are received');
      qc.invalidateQueries({ queryKey: ['order', orderId] });
      qc.invalidateQueries({ queryKey: ['order-timeline', orderId] });
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Consolidation failed'),
  });

  // Aggregate ship-now per product, then flag any product short in the chosen warehouse.
  const shortLines = useMemo(() => {
    if (!whCode) return [] as string[];
    const want = new Map<string, number>();
    for (const it of items) {
      const q = Number(qtys[it.id] ?? 0);
      if (q > 0) want.set(String((it as any).productId), (want.get(String((it as any).productId)) ?? 0) + q);
    }
    const short: string[] = [];
    for (const [pid, q] of want) if (q > (whStockByProduct.get(pid) ?? 0) + 1e-6) short.push(pid);
    return short;
  }, [items, qtys, whCode, whStockByProduct]);
  const hasShortage = shortLines.length > 0;

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={order ? `Pick & pack — ${order.orderNo}` : 'Pick & pack'}
      width={860}
      footer={
        <>
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={send.isPending}
            disabled={totalThisDispatch <= 0 || !warehouseId || !allBatchAllocated || !withinPending}
            onClick={() => form.submit()}>
            {`Pack ${fmtQty(totalThisDispatch)} unit${totalThisDispatch === 1 ? '' : 's'}`}
          </Button>
        </>
      }
    >
      <Alert
        type="info"
        style={{ marginBottom: 12 }}
        title="Pick the source batch(es) for each line — supports full or partial packs."
        description="Set the quantity per line, then choose the batch(es) to pick from (Auto FEFO fills the earliest-expiry batches). Stock leaves the warehouse and is committed now, at pack — the order moves to PACKED. Add logistics below (optional). Dispatch the packed goods separately, after the Tally invoice."
      />

      {/* Where to send it — the packer arranges transport here, so surface the consignee up front. The
          order has no separate ship-to; delivery goes to the dealer's registered address. */}
      <Alert
        type={order?.retailerAddress ? 'info' : 'warning'}
        icon={<MapPin size={16} />}
        style={{ marginBottom: 12 }}
        title={<span>Deliver to: <b>{order?.retailerName ?? (order?.retailerId ? `#${order.retailerId}` : '—')}</b>{order?.retailerMobile ? <> · <a href={`tel:${order.retailerMobile}`}>{order.retailerMobile}</a></> : null}</span>}
        description={order?.retailerAddress
          ? (
            <Space size="middle" wrap>
              <span>{order.retailerAddress}</span>
              <a target="_blank" rel="noopener" href={order.retailerLat && order.retailerLng
                ? `https://www.google.com/maps?q=${order.retailerLat},${order.retailerLng}`
                : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(order.retailerAddress)}`}>View map</a>
            </Space>
          )
          : 'No delivery address on file for this dealer — confirm with sales before dispatching.'}
      />

      <Form form={form} layout="vertical" onFinish={(v) => send.mutate(v)}>
        <Form.Item label="Dispatch warehouse" required style={{ marginBottom: 12 }}
          validateStatus={!warehouseId ? 'warning' : undefined}
          help={!warehouseId && !order?.dispatchWarehouseId ? 'Pick the warehouse to ship from' : undefined}>
          {order?.dispatchWarehouseId ? (
            // Locked: the fulfillment warehouse was chosen at approval; the order ships from there.
            <Space wrap>
              <Tag tone="blue" style={{ fontSize: 13, padding: '2px 10px', margin: 0 }}>
                {assignedWh ? `${assignedWh.code} · ${assignedWh.name}` : (order?.dispatchWarehouseName ?? whCode ?? '—')}
              </Tag>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>Assigned at approval — this order ships from here.</Typography.Text>
            </Space>
          ) : (
            <Select
              showSearch optionFilterProp="label" placeholder="Select warehouse to ship from"
              value={warehouseId} onChange={setWarehouseId} style={{ maxWidth: 380 }}
              options={dispatchWarehouses.map((w: any) => ({ value: String(w.id), label: `${w.code} · ${w.name}`, isMaster: !!w.isMaster }))}
            />
          )}
        </Form.Item>

        <Typography.Title level={5}>Lines</Typography.Title>
        <Table
          size="small"
          pagination={false}
          rowKey={(r: any) => String(r.id)}
          dataSource={items}
          expandable={{
            // Keys MUST be strings to match shippingItemIds (String(id)); a numeric id here would
            // never match the string keys and the per-line batch allocator would silently never open.
            expandedRowKeys: warehouseId ? shippingItemIds : [],
            showExpandColumn: false,
            expandedRowRender: (r: any) => (warehouseId && Number(qtys[r.id] ?? 0) > 0) ? (
              <BatchAllocRow warehouseId={warehouseId} item={r} qty={Number(qtys[r.id] ?? 0)}
                alloc={alloc[r.id] ?? {}}
                onChange={(batchId, q) => setAlloc((s) => ({ ...s, [r.id]: { ...(s[r.id] ?? {}), [batchId]: q } }))}
                onAuto={(m) => setAlloc((s) => ({ ...s, [r.id]: m }))} />
            ) : null,
          }}
          columns={[
            {
              title: 'Product',
              render: (_: any, r: any) => (
                <div>
                  <div>{r.productNameSnapshot}{r.variantLabel ? ` ${r.variantLabel}` : ''}</div>
                  {r.sku ? <div style={{ fontSize: 12, color: '#8c8c8c' }}>{r.sku}</div> : null}
                </div>
              ),
            },
            {
              title: 'Ordered',
              dataIndex: 'qty',
              align: 'right' as const,
              width: 90,
              render: (v: string) => fmtQty(v),
            },
            {
              title: 'Already shipped',
              align: 'right' as const,
              width: 130,
              render: (_: any, r: OrderItem) =>
                fmtQty(Number(r.qty) - Number(pending?.[r.id] ?? 0)),
            },
            {
              title: 'Pending',
              align: 'right' as const,
              width: 100,
              render: (_: any, r: OrderItem) => {
                const p = Number(pending?.[r.id] ?? 0);
                return p > 0 ? <Tag tone="gold">{fmtQty(p)}</Tag> : <Tag tone="green">0</Tag>;
              },
            },
            {
              title: whCode ? `In ${whCode}` : 'In warehouse',
              align: 'right' as const,
              width: 130,
              render: (_: any, r: OrderItem) => {
                if (!whCode) return <span style={{ color: '#bbb' }}>—</span>;
                const avail = whStockByProduct.get(String((r as any).productId)) ?? 0;
                const shipNow = Number(qtys[r.id] ?? 0);
                const pk = packagingByProduct.get(String((r as any).productId));
                const bl = pk?.enabled ? displayBoxLoose(avail, pk.upb) : '';
                return (
                  <div>
                    <Tag tone={shipNow > avail ? 'red' : avail > 0 ? 'green' : 'neutral'} style={{ margin: 0 }}>{fmtQty(avail)}</Tag>
                    {bl && <div style={{ fontSize: 11, color: '#8c8c8c' }}>{bl}</div>}
                  </div>
                );
              },
            },
            {
              title: 'Ship now',
              align: 'right' as const,
              width: 200,
              render: (_: any, r: OrderItem) => {
                const max = Number(pending?.[r.id] ?? 0);
                const avail = whCode ? (whStockByProduct.get(String((r as any).productId)) ?? 0) : Infinity;
                const shipNow = Number(qtys[r.id] ?? 0);
                const pk = packagingByProduct.get(String((r as any).productId));
                if (pk?.enabled) {
                  const bl = blFor(r.id, pk.upb);
                  const over = shipNow > max + 1e-6;
                  return (
                    <div>
                      <Space size={4}>
                        <InputNumber size="small" min={0} precision={0} value={bl.box} disabled={max <= 0} onChange={(v) => setBox(r.id, pk.upb, Number(v ?? 0))} style={{ width: 54 }} />
                        <span style={{ fontSize: 11, color: '#999' }}>box</span>
                        <InputNumber size="small" min={0} value={bl.loose} disabled={max <= 0} onChange={(v) => setLoose(r.id, pk.upb, Number(v ?? 0))} style={{ width: 54 }} />
                        <span style={{ fontSize: 11, color: '#999' }}>loose</span>
                      </Space>
                      <div style={{ fontSize: 11, color: over || shipNow > avail ? '#cf1322' : '#8c8c8c' }}>
                        = {fmtQty(shipNow)} units{over ? ` · max ${fmtQty(max)}` : ` · ${pk.upb}/box`}
                      </div>
                    </div>
                  );
                }
                return (
                  <InputNumber
                    min={0}
                    max={max}
                    step={1}
                    value={qtys[r.id] ?? 0}
                    disabled={max <= 0}
                    status={shipNow > avail ? 'error' : undefined}
                    onChange={(v) => setQtys((m) => ({ ...m, [r.id]: Number(v ?? 0) }))}
                    style={{ width: '100%' }}
                  />
                );
              },
            },
            {
              title: 'Batch',
              align: 'right' as const,
              width: 110,
              render: (_: any, r: OrderItem) => {
                const q = Number(qtys[r.id] ?? 0);
                if (q <= 0) return <span style={{ color: '#bbb' }}>—</span>;
                const a = allocatedFor(r.id);
                return <Tag tone={Math.abs(a - q) < 1e-6 ? 'green' : 'gold'}>{fmtQty(a)}/{fmtQty(q)}</Tag>;
              },
            },
          ]}
        />
        <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: hasShortage ? 8 : 16 }}>
          Pending across all lines: <b>{fmtQty(totalPending)}</b> · Shipping now: <b>{fmtQty(totalThisDispatch)}</b>
        </Typography.Paragraph>
        {hasShortage && !consolidationFeasible && (
          <Alert type="error" style={{ marginBottom: 16 }}
            title={`Not enough stock in ${whCode} for ${shortLines.length} line${shortLines.length === 1 ? '' : 's'}.`}
            description="Reduce the ship-now quantity or pick a warehouse that has stock. You can dispatch the rest later (partial shipment)." />
        )}
        {consolidationFeasible && warehouseId && (
          <Alert type="warning" style={{ marginBottom: 16 }}
            title={`Stock is split across warehouses — ${whCode} alone can't cover this order.`}
            description={
              <div>
                <div style={{ marginBottom: 8 }}>
                  The combined stock across warehouses <b>does</b> cover it. Consolidate into <b>{whCode}</b> — the
                  system transfers the shortfall in (stock goes in transit), the order is held until the transfers are
                  received, then you dispatch the full quantity as one shipment.
                </div>
                {(plan ?? []).map((p: any) => (
                  <div key={p.fromWarehouseId} style={{ fontSize: 13 }}>
                    • <b>{p.fromName}</b> → {whCode}: {p.lines.map((l: any) => `${l.productName ?? l.productId} ×${fmtQty(l.qty)}`).join(', ')}
                  </div>
                ))}
                <Button variant="primary" icon={<ArrowLeftRight size={16} />} style={{ marginTop: 10 }}
                  loading={consolidate.isPending} disabled={!(plan ?? []).length}
                  onClick={() => consolidate.mutate()}>
                  Consolidate to {whCode} &amp; hold order
                </Button>
              </div>
            } />
        )}

        <Divider style={{ margin: '8px 0' }} />
        <Typography.Title level={5}>Logistics</Typography.Title>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="carrier" label="Carrier / transporter">
              <Input placeholder="e.g. Delhivery, VRL, BlueDart" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="lrNo" label="LR / docket no">
              <Input placeholder="LR number" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="vehicleNo" label="Vehicle no">
              <Input placeholder="MH-04-AB-1234" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="driverName" label="Driver name">
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="driverMobile" label="Driver mobile">
              <Input placeholder="9876543210" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="expectedAt" label="Expected delivery">
              <DatePicker showTime={{ use12Hours: true, format: 'hh:mm A' }} format="DD MMM YYYY hh:mm A" className="w-full" disabledDate={(d) => d.isBefore(dayjs().startOf('day'))} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// Per-line source-batch allocator: the packer chooses which batch(es) at the dispatch warehouse
// cover the line's ship qty. Auto FEFO fills earliest-expiry batches first. Allocated qty must equal
// the ship qty before dispatch is enabled (enforced in the parent + re-validated server-side).
function BatchAllocRow({ warehouseId, item, qty, alloc, onChange, onAuto }: {
  warehouseId: string; item: any; qty: number;
  alloc: Record<string, number>; onChange: (batchId: string, qty: number) => void; onAuto: (m: Record<string, number>) => void;
}) {
  const { data: batches } = useQuery({
    queryKey: ['dispatch-source-batches', warehouseId, item.productId],
    queryFn: () => request<any[]>({ url: '/transfer-source-batches', params: { warehouseId, productId: item.productId } }),
    enabled: !!warehouseId && !!item.productId,
  });
  const list = batches ?? [];
  const allocated = Object.values(alloc).reduce((s, q) => s + (Number(q) || 0), 0);
  const autoFefo = () => {
    let remaining = qty;
    const next: Record<string, number> = {};
    for (const b of list) {
      if (remaining <= 1e-9) break;
      const take = Math.min(Number(b.available), remaining);
      if (take > 0) { next[String(b.batchId)] = take; remaining -= take; }
    }
    onAuto(next);
  };
  return (
    <div>
      <Space style={{ marginBottom: 8 }} wrap>
        <Typography.Text strong>{item.productNameSnapshot ?? 'Product'} — allocate {fmtQty(qty)} across source batches (FEFO):</Typography.Text>
        <Button size="sm" onClick={autoFefo} disabled={!list.length}>Auto FEFO</Button>
        <Tag tone={Math.abs(allocated - qty) < 1e-6 ? 'green' : 'gold'}>{fmtQty(allocated)} / {fmtQty(qty)}</Tag>
      </Space>
      {!list.length && (
        <Alert type="warning" style={{ marginBottom: 8 }}
          title="No saleable (unexpired) stock for this product here — reduce the qty, pick another warehouse, or consolidate first." />
      )}
      <Table size="small" pagination={false} rowKey="batchId" dataSource={list}
        locale={{ emptyText: 'No saleable stock for this product in the selected warehouse' }}
        columns={[
          { title: 'Batch', dataIndex: 'batchNo' },
          { title: 'Expiry', dataIndex: 'expDate', render: (v: any) => (v ? formatIstDate(v) : '—') },
          { title: 'Available', dataIndex: 'available', align: 'right' as const, render: (v: any) => fmtQty(v) },
          {
            title: 'Allocate', align: 'right' as const,
            render: (_: any, b: any) => (
              <InputNumber min={0} max={Number(b.available)} value={alloc[String(b.batchId)] ?? 0}
                onChange={(v) => onChange(String(b.batchId), Number(v ?? 0))} />
            ),
          },
        ]} />
    </div>
  );
}
